/**
 * One side's clock.
 *
 * The mechanism is the reason a tick costs one text node: the interval lives in
 * this leaf, it only forces a render when the VISIBLE face changes (9 of 10
 * ticks it does not), and NOTHING is written to the store.
 *
 * E4: the face is DERIVED during the render rather than held in state. It used
 * to be two `useState`s that the effect re-applied on every start/stop, so the
 * side-to-move clock paid two renders per move — one for the store write, one
 * for the effect's `apply()` right after it. Now the store write alone carries
 * the new face, and the interval is the only thing that can add a render.
 */
import {useEffect, useMemo, useRef, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';
import {
  selectBoardSource,
  selectClock,
  selectWatchClock,
} from '@/Store/liveGame/selectors';
import type {Side} from '@/Store/liveGame/state';

import {LOW_TIME_MS, format, remainingMs} from './format';

/** tenths are visible under 10 s, so the face is re-read ten times a second */
const TICK_MS = 100;

export type ClockModel = {
  text: string;
  low: boolean;
  active: boolean;
};

export function useClockModel(side: Side): ClockModel {
  /**
   * M4: a WATCHED clock also ticks — the old screen hands it to the same
   * `SimpleOptimizedClock` with `playing={watchState !== 8}` (index.js
   * L4706-4719), so `selectWatchClock` returns a `runningSince` for the side to
   * move and null once the game is over or the board is a static replay.
   */
  const source = useLiveGame(selectBoardSource);
  const selector = useMemo(
    () => (source === 'watch' ? selectWatchClock(side) : selectClock(side)),
    [source, side],
  );
  const {
    remainingMs: baseMs,
    runningSince,
    active,
  } = useLiveGame(useShallow(selector));

  /** the tick counter is write-only: it exists to invalidate this render */
  const [, setTick] = useState(0);

  const left = remainingMs(baseMs, runningSince);
  const text = format(left);
  const low = left < LOW_TIME_MS;

  /**
   * What is on screen right now, so the interval can tell a tick that moves the
   * face from the 9 in 10 that do not. Written during the render (the face IS
   * this render's output) and by the interval itself.
   */
  const face = useRef('');
  face.current = `${text}|${low}`;

  useEffect(() => {
    if (runningSince === null) {
      return undefined;
    }
    const interval = setInterval(() => {
      const ms = remainingMs(baseMs, runningSince);
      const next = `${format(ms)}|${ms < LOW_TIME_MS}`;
      if (next !== face.current) {
        face.current = next;
        setTick(tick => tick + 1);
      }
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [baseMs, runningSince]);

  return {text, low, active};
}
